"use client"

import React, { useState, useEffect } from "react"
import axios from "axios"
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material"
import {
  Home as HomeIcon,
  CalendarToday as CalendarTodayIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

const ConsulterStock = () => {
  const navigate = useNavigate()

  // State to store raw stock data from API
  const [stockData, setStockData] = useState([])
  const baseUrl = process.env.REACT_APP_APP_URL
  // State to store processed inventory data
  const [inventoryData, setInventoryData] = useState([])

  // State to store operations history
  const [operations, setOperations] = useState([])

  useEffect(() => {
    const fetchStockData = async () => {
      const token = localStorage.getItem("CC_Token")
      try {
        const response = await axios.get(`${baseUrl}/api/stock`, { 
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        setStockData(response.data)

        // Process the data to generate inventory and operations
        processStockData(response.data)
      } catch (error) {
        console.error("Error fetching stock data:", error)
        toast.error("Erreur lors de la récupération des données du stock.", {
          position: "top-right",
          autoClose: 3000,
        })
      }
    }

    fetchStockData()
  }, [])

  // Process stock data to calculate inventory levels and operations history
  const processStockData = (data) => {
    // Group data by merchandise
    const groupedByMerchandise = {}
    const operationsHistory = []

    // Process each stock entry
    data.forEach((item) => {
      // Add to operations history
      operationsHistory.push({
        id: item.id,
        date: item.date,
        merchandise: item.Marchandise,
        quantity: item.Quantité,
        description: item.Description,
        amount: item.Montant ? `${item.Statut === "IN" ? "-" : "+"}${item.Montant} DT` : "0 DT",
        status: item.Statut,
      })

      // Process inventory calculations
      if (!groupedByMerchandise[item.Marchandise]) {
        groupedByMerchandise[item.Marchandise] = {
          name: item.Marchandise,
          inQuantity: 0,
          outQuantity: 0,
          remainingQuantity: 0,
        }
      }

      // Update quantities based on status
      if (item.Statut === "IN") {
        groupedByMerchandise[item.Marchandise].inQuantity += Number.parseInt(item.Quantité)
      } else if (item.Statut === "OUT") {
        groupedByMerchandise[item.Marchandise].outQuantity += Number.parseInt(item.Quantité)
      }
    })

    // Calculate remaining quantities
    Object.keys(groupedByMerchandise).forEach((key) => {
      groupedByMerchandise[key].remainingQuantity =
        groupedByMerchandise[key].inQuantity - groupedByMerchandise[key].outQuantity
    })

    // Convert to array for rendering
    const inventoryArray = Object.values(groupedByMerchandise)

    // Sort operations by date (newest first)
    operationsHistory.sort((a, b) => new Date(b.date) - new Date(a.date))

    setInventoryData(inventoryArray)
    setOperations(operationsHistory)
  }

  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs")
  }

  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    localStorage.removeItem("user")
    toast.success("Vous avez été déconnecté avec succès.", {
      position: "top-right",
      autoClose: 3000,
    })
    setTimeout(() => navigate("/login"), 4000)
  }

  const handleProfile = () => {
    navigate("/ProfileAdmin")
  }

  const handleRefresh = async () => {
    const token = localStorage.getItem("CC_Token")
    try {
      const response = await axios.get(`${baseUrl}/api/stock`, { 
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setStockData(response.data)
      processStockData(response.data)
      toast.info("Données rafraîchies!", { position: "top-right", autoClose: 2000 })
    } catch (error) {
      console.error("Error refreshing stock data:", error)
      toast.error("Erreur lors du rafraîchissement des données.", {
        position: "top-right",
        autoClose: 3000,
      })
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  }

  return (
    <div className="app">
      <ToastContainer />

      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar alt="Logo" src={"../assets/cej.png"} sx={{ width: 40, height: 40, marginRight: "0.5rem" }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: { xs: "1rem", sm: "1.25rem" }, color: "#ffffff" }}>
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          padding: "1rem",
          backgroundColor: "#f8f8f8",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          marginTop: "64px",
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: "600", textAlign: "center", color: "#333", marginBottom: "1.5rem" }}>
          Gestion du Stock et Opérations
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "1.5rem" }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ backgroundColor: "#004080", borderRadius: "5px", padding: "0.5rem 1rem", fontSize: "0.875rem" }}
          >
            Rafraîchir
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ marginBottom: "1.5rem", borderRadius: "8px", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)" }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#003366" }}>
                <TableCell sx={{ color: "#ffffff", fontWeight: "600" }}>Article</TableCell>
                <TableCell sx={{ color: "#ffffff", fontWeight: "600" }}>Quantité Entrante</TableCell>
                <TableCell sx={{ color: "#ffffff", fontWeight: "600" }}>Quantité Sortante</TableCell>
                <TableCell sx={{ color: "#ffffff", fontWeight: "600" }}>Quantité Restante</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventoryData.map((item, index) => (
                <TableRow key={index} sx={{ "&:nth-of-type(odd)": { backgroundColor: "#f8f8f8" } }}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.inQuantity}</TableCell>
                  <TableCell>{item.outQuantity}</TableCell>
                  <TableCell>{item.remainingQuantity}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Paper
          sx={{
            padding: "1rem",
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "600", marginBottom: "1rem" }}>
            Opérations
          </Typography>
          <List>
            {operations.map((operation) => (
              <React.Fragment key={operation.id}>
                <ListItem>
                  <ListItemText
                    primary={`${operation.merchandise} - ${operation.status === "IN" ? "Entrée" : "Sortie"} de ${operation.quantity} unités`}
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          Date: {formatDate(operation.date)}
                        </Typography>
                        <br />
                        {operation.description && (
                          <>
                            <Typography component="span" variant="body2" color="text.primary">
                              Description: {operation.description}
                            </Typography>
                            <br />
                          </>
                        )}
                        <Typography component="span" variant="body2" color="text.primary">
                          Montant: {operation.amount}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>

      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{ backgroundColor: "#003366", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminChrono")}
          />
          <BottomNavigationAction
            label="Home"
            icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/Admin")}
          />
          <BottomNavigationAction
            label="Paiement"
            icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminPaiment")}
          />
        </BottomNavigation>
      </Box>
    </div>
  )
}

export default ConsulterStock

